import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { type CSSProperties, type FC, type HTMLAttributes, type ReactElement, type ReactNode, useRef } from "react";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import styleSkeleton from "../../styles/loading_skeleton.module.css";
import { TruncateText } from "../FitTruncate/TruncateText.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { TagIntent } from "../Tag/settings.ts";
import { Tag, type TagProps } from "../Tag/Tag.tsx";
import style from "./HeaderBar.module.css";

export interface HeaderBarProps {
  title?: string | ReactElement;
  subtitle?: string | ReactElement | null;
  /** Shown before the title and subtitle. */
  icon?: ReactElement | MaterialSymbol;
  infoComponent?: ReactNode;
  /**
   * List of actions on the right side of the HeaderBar.
   * Usually TriggerButton or HeaderDivider.
   * Like a close button for Dialog.
   */
  actions?: ReactNode[] | null;
  domTitleProps?: HTMLAttributes<HTMLDivElement>;
  className?: string;
  /** Some info on the right of title/subtitle */
  tag?: TagProps;
  style?: CSSProperties;
  tipTourId?: string;

  /** Skeleton for the tile */
  isLoading?: boolean;
  asPanelHeader?: boolean;
  titleTruncateMiddle?: boolean;
}

export const HeaderBar: FC<HeaderBarProps> = (props) => {
  // Empty string are falsy

  const hasLeft = props.icon || props.title || props.subtitle || props.tag?.label;
  const ref = useRef<HTMLDivElement>(null);
  const dataAttributes = useDataAttributes(props);
  const testId = `${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}header-bar`;

  if (!hasLeft && !props.actions) return null;

  return (
    <div
      ref={ref}
      className={clsx(style.bar, props.className, {
        [style.toolbar]: !hasLeft,
        [style.with_icon]: !!props.icon,
        [style.as_panel_header]: !!props.asPanelHeader,
      })}
      style={props.style}
      {...dataAttributes}
      data-testid={testId}
      data-e2e-id={props.title}
    >
      {!!hasLeft && (
        <HStack style={{ flex: 1, overflow: "hidden" }}>
          {props.icon != undefined && <Icon icon={props.icon} className={style.icon} />}
          <VStack spacing={1} style={{ overflow: "hidden" }}>
            {props.titleTruncateMiddle && typeof props.title === "string" ? (
              <TruncateText className={style.truncated_title} text={props.title} />
            ) : (
              <div className={style.title} {...props.domTitleProps}>
                {props.isLoading ? <span className={styleSkeleton.skeleton}>Lorem ipsum dolor</span> : props.title}
                {props.tipTourId != undefined && <span id={props.tipTourId} className="hotspot-beacon" />}
              </div>
            )}
            {props.subtitle != undefined && <div className={style.subtitle}>{props.subtitle}</div>}
          </VStack>
          {props.tag?.label != undefined && (
            <Tag {...props.tag} intentColor={props.tag.intentColor ?? TagIntent.Outlined} />
          )}
          {props.infoComponent && (
            <>
              <Spacer fixedWidth={20} />
              {props.infoComponent}
              {/* <HeaderInfoAttributes attributes={props.infoAttributes} /> */}
            </>
          )}
        </HStack>
      )}
      <div className={clsx(style.actions, { [style.actions_only]: !hasLeft })} data-testid={"header-bar-actions"}>
        {props.actions}
      </div>
    </div>
  );
};
